import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { CreateFolderEventAdapter } from '~analytics/adapters/CreateFolderEventAdapter';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { useCreateFolder } from '~teacher/hook/useCreateFolder';
import { quizzesQueryKey } from '~teacher/hook/useQuizzes';

import { FolderType } from '../../../../api/folders';

/**
 * Clears the folder name when the modal is opened.
 */
function ResetModalForm({
  open,
  setNewFolderName,
  setIsDisabled,
}: {
  open: boolean;
  setNewFolderName: React.Dispatch<React.SetStateAction<string>>;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  React.useEffect(() => {
    if (open) {
      setNewFolderName(translate('Untitled Folder'));
      setIsDisabled(false);
    }
  }, [open, setNewFolderName, setIsDisabled]);
}

function useCreateNewFolder({
  parentId,
  onClose,
  newFolderName,
}: {
  parentId: number;
  onClose: () => void;
  newFolderName: string;
}) {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateFolder();
  const saveFolder = (folderName: string) => {
    mutate(
      {
        name: folderName,
        parentId: parentId,
        type: FolderType.QUIZ_FOLDER,
      },
      {
        onSuccess: () => {
          new CreateFolderEventAdapter().sendAnalytics();
        },
        onSettled: () => {
          queryClient.invalidateQueries(quizzesQueryKey.quiz);
          onClose();
        },
      }
    );
  };

  const createNewFolder = () => saveFolder(newFolderName);

  return { createNewFolder, isLoading };
}

interface Props extends Pick<DialogProps, 'open'> {
  parentId: number;
  onClose: () => void;
}

export function AddFolderDialog({ onClose, parentId, open }: Props) {
  const [newFolderName, setNewFolderName] = React.useState<string>();
  const [isDisabled, setIsDisabled] = React.useState<boolean>(false);

  const onFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setNewFolderName(event.target.value);

  ResetModalForm({
    open: open,
    setNewFolderName: setNewFolderName,
    setIsDisabled: setIsDisabled,
  });

  const { createNewFolder } = useCreateNewFolder({
    parentId: parentId,
    onClose: onClose,
    newFolderName: newFolderName,
  });

  const autoFocusOnOpenCallback = useAutoFocus();

  return (
    <Dialog
      onClose={onClose}
      id="add-folder-modal"
      open={open}
      title={translate('New Folder')}
      closeLabel={translate('Cancel')}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="folder-name">{translate('Folder name')}</label>
        <TextInput
          maxLength={255}
          ref={autoFocusOnOpenCallback}
          id="folder-name"
          defaultValue={newFolderName}
          onChange={onFolderNameChange}
          className="mt-3"
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
          <UIButton
            appearance="primary"
            isDisabled={isDisabled}
            onClick={() => {
              setIsDisabled(true);
              createNewFolder();
            }}
          >
            {translate('Create')}
          </UIButton>
        </Inline>
      </div>
    </Dialog>
  );
}
